import { useState, useEffect, useCallback, memo } from "react";
import api from "../utils/api";
import { sensorLage, windColor } from "../utils/wind";
import SimulationTab from "./SimulationTab";

// ── Farben pro Sensor (gleich wie WindCharts) ────────────────────
const SENSOR_COLORS = [
  "#22c55e","#3b82f6","#f97316","#a855f7","#eab308",
  "#ef4444","#06b6d4","#f43f5e","#84cc16","#0ea5e9",
];

const PRESETS = [
  { label: "24 Std.", hours: 24 },
  { label: "48 Std.", hours: 48 },
  { label: "7 Tage",  hours: 168 },
  { label: "14 Tage", hours: 336 },
  { label: "30 Tage", hours: 720 },
  { label: "90 Tage", hours: 2160 },
];

// Schenker AS150 Ausstellstore – Betriebsstufen (Max. 60 km/h = 16.7 m/s)
const WIND_CLASSES = [
  {
    key: "pct_over_8_3",
    ms: 8.3,
    kmh: 30,
    label: "> 8.3 m/s (30 km/h)",
    shortLabel: "30 km/h · 8.3 m/s",
    color: "#eab308",
    status: "Warnung – Windklasse 1",
    desc: "Vorsicht · Store beobachten · EN 13561 Kl. 1",
    icon: "🟡",
  },
  {
    key: "pct_over_11_1",
    ms: 11.1,
    kmh: 40,
    label: "> 11.1 m/s (40 km/h)",
    shortLabel: "40 km/h · 11.1 m/s",
    color: "#f97316",
    status: "Einfahren empfohlen – Windklasse 2",
    desc: "Store sollte eingefahren werden · EN 13561 Kl. 2",
    icon: "🟠",
  },
  {
    key: "pct_over_13_9",
    ms: 13.9,
    kmh: 50,
    label: "> 13.9 m/s (50 km/h)",
    shortLabel: "50 km/h · 13.9 m/s",
    color: "#ef4444",
    status: "Zwangseinfahrt – Windklasse 3",
    desc: "Store muss eingefahren sein · EN 13561 Kl. 3",
    icon: "🔴",
  },
  {
    key: "pct_over_16_7",
    ms: 16.7,
    kmh: 60,
    label: "> 16.7 m/s (60 km/h)",
    shortLabel: "60 km/h · 16.7 m/s",
    color: "#a855f7",
    status: "Produktlimit – Windklasse 4",
    desc: "Max. Zulassung AS150 erreicht · EN 13561 Kl. 4",
    icon: "⛔",
  },
];

function fmt(v, decimals = 1) {
  if (v === null || v === undefined) return "–";
  return Number(v).toFixed(decimals);
}

function fmtDt(iso) {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("de-CH", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function fmtMin(min) {
  if (!min) return "0 Min.";
  if (min < 60) return `${Math.round(min)} Min.`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

// ── Stundenprofil-Balken-Chart (SVG) ─────────────────────────────
function HourlyChart({ data, sensorNames, sensorColors, visibleSensors }) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const allVals = sensorNames.flatMap(name =>
    visibleSensors[name] ? (data[name]?.hourly || []).map(h => h.avg_wind || 0) : []
  );
  const vMax = Math.max(...allVals, 1);

  const W = 760, H = 140, padL = 36, padB = 24, padT = 8, padR = 8;
  const gW = W - padL - padR;
  const gH = H - padT - padB;
  const barW = gW / 24;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}>
      {/* Y-Gitter */}
      {[0, 0.25, 0.5, 0.75, 1].map(f => {
        const y = padT + gH * (1 - f);
        const v = (vMax * f).toFixed(1);
        return (
          <g key={f}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#1e3a5f" strokeDasharray="3,3" />
            <text x={padL - 4} y={y + 4} textAnchor="end" fill="#4a6a8a" fontSize="9" fontFamily="sans-serif">{v}</text>
          </g>
        );
      })}
      {/* Balken pro Sensor */}
      {sensorNames.map((name, si) => {
        if (!visibleSensors[name]) return null;
        const hourly = data[name]?.hourly || [];
        const color = sensorColors[si] || "#94a3b8";
        const n = sensorNames.filter(n => visibleSensors[n]).length;
        const idx = sensorNames.filter((_, i) => i <= si && visibleSensors[sensorNames[i]]).length - 1;
        const subW = (barW - 4) / n;
        return hourly.map(h => {
          if (h.avg_wind === null) return null;
          const barH = (h.avg_wind / vMax) * gH;
          const x = padL + h.hour * barW + 2 + idx * subW;
          const y = padT + gH - barH;
          return (
            <rect key={`${name}-${h.hour}`} x={x} y={y} width={subW - 1} height={barH}
              fill={color} opacity={0.75} rx={1} />
          );
        });
      })}
      {/* X-Achse Stunden */}
      {[0, 3, 6, 9, 12, 15, 18, 21, 23].map(h => {
        const x = padL + h * barW + barW / 2;
        return (
          <text key={h} x={x} y={H - 4} textAnchor="middle" fill="#4a6a8a" fontSize="9" fontFamily="sans-serif">
            {h}:00
          </text>
        );
      })}
      {/* Achsen */}
      <line x1={padL} y1={padT} x2={padL} y2={padT + gH} stroke="#2a4a6a" strokeWidth="1.5" />
      <line x1={padL} y1={padT + gH} x2={W - padR} y2={padT + gH} stroke="#2a4a6a" strokeWidth="1.5" />
      <text x={8} y={padT + gH / 2} textAnchor="middle" fill="#4a6a8a" fontSize="9" fontFamily="sans-serif"
        transform={`rotate(-90, 8, ${padT + gH / 2})`}>m/s</text>
    </svg>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────
const AnalysisTab = memo(function AnalysisTab({ sensors, visibleSensors, setVisibleSensors }) {
  const [activePreset, setActivePreset] = useState("24 Std.");
  const [customMode, setCustomMode] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [hours, setHours] = useState(24);
  const [threshold, setThreshold] = useState(8.0);
  const [thresholdInput, setThresholdInput] = useState("8.0");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [earliest, setEarliest] = useState(null);
  // visibleSensors kommt als Prop von Dashboard (stabil über Refreshes)
  const [activeSection, setActiveSection] = useState("dist"); // dist | classes | simulate | threshold | hourly | stats

  const activeSensors = sensors.filter(s => s.is_active !== false);
  const sensorNames = activeSensors.map(s => s.name);
  // Tabellen-Sortierung: nach Lage im Gebaeude (oben zuerst: Dach, DG, 6.OG ... EG),
  // abgeleitet aus position_y/x wie die Stockwerk-Beschriftung selbst.
  const sortedSensors = [...activeSensors].sort((a, b) =>
    ((a.position_y ?? 999) - (b.position_y ?? 999)) ||
    ((a.position_x ?? 0) - (b.position_x ?? 0)) ||
    a.name.localeCompare(b.name, undefined, { numeric: true })
  );
  // Sensor-Farbe stabil an den Namen gebunden (unabhaengig von Sortierung/Auswahl)
  const colorOf = (name) => SENSOR_COLORS[Math.max(0, sensorNames.indexOf(name)) % SENSOR_COLORS.length];

  // Init
  useEffect(() => {
    setVisibleSensors(prev => {
      const v = { ...prev };
      activeSensors.forEach(s => { if (!(s.name in v)) v[s.name] = true; });
      return v;
    });
    api.get("/measurements/data-range").then(r => {
      if (r.data.earliest) setEarliest(r.data.earliest.substring(0, 16));
    }).catch(() => {});
  }, [sensors.length]);

  const getDateRange = useCallback(() => {
    if (customMode && dateFrom && dateTo) {
      return {
        from: new Date(dateFrom).toISOString(),
        to: new Date(dateTo).toISOString(),
      };
    }
    return {
      from: new Date(Date.now() - hours * 3600000).toISOString(),
      to: new Date().toISOString(),
    };
  }, [customMode, dateFrom, dateTo, hours]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange();
      const r = await api.get(
        `/measurements/analyze?date_from=${from}&date_to=${to}&threshold=${threshold}`
      );
      setData(r.data);
      setLoadError(false);
    } catch (e) {
      console.error(e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, threshold]);

  useEffect(() => { loadData(); }, [hours, customMode, dateFrom, dateTo, threshold]);

  const toggleSensor = (name) => {
    setVisibleSensors(v => ({ ...v, [name]: !v[name] }));
  };

  const handleThresholdChange = (val) => {
    setThresholdInput(val);
    const n = parseFloat(val);
    if (!isNaN(n) && n >= 0) setThreshold(n);
  };

  // ── CSV Export Wind ──────────────────────────────────────────────
  const handleExport = async () => {
    try {
      const { from, to } = getDateRange();
      const sel = sensors.filter(s => visibleSensors[s.name]).map(s => s.name);
      const sensorParam = sel.length > 0 && sel.length < sensors.length
        ? `&sensors=${encodeURIComponent(sel.join(","))}` : "";
      const url = `/measurements/export-csv?date_from=${from}&date_to=${to}${sensorParam}`;
      const r = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      const fromStr = from.substring(0, 10);
      const toStr = to.substring(0, 10);
      a.download = `windmess_wind_${fromStr}_${toStr}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert("Export fehlgeschlagen");
    }
  };

  // ── CSV Export Alles ─────────────────────────────────────────────
  const handleExportFull = async () => {
    try {
      const { from, to } = getDateRange();
      const sel = sensors.filter(s => visibleSensors[s.name]).map(s => s.name);
      const sensorParam = sel.length > 0 && sel.length < sensors.length
        ? `&sensors=${encodeURIComponent(sel.join(","))}` : "";
      const url = `/measurements/export-csv-full?date_from=${from}&date_to=${to}${sensorParam}`;
      const r = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      const fromStr = from.substring(0, 10);
      const toStr = to.substring(0, 10);
      a.download = `windmess_alles_${fromStr}_${toStr}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert("Export fehlgeschlagen");
    }
  };

  const visibleCount = Object.values(visibleSensors).filter(Boolean).length;

  return (
    <div style={S.page}>
      {/* ── Kopfzeile ── */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <span style={S.title}>Windanalyse</span>
          {data && (
            <span style={S.subtitle}>
              {Object.entries(data).filter(([n]) => visibleSensors[n]).reduce((sum, [, d]) => sum + (d.count || 0), 0).toLocaleString()} Messpunkte
              {" · "}{visibleCount}/{sensors.length} Sensoren
            </span>
          )}
        </div>
        <div style={S.headerRight}>
          <button onClick={handleExport} style={S.exportBtn}>
            ⬇ CSV Export Wind
          </button>
          <button onClick={handleExportFull} style={S.exportBtnFull}>
            ⬇ CSV Export Alles
          </button>
          {loading && <span style={{ ...S.loadingText, fontSize: 13, color: "#60a5fa", fontWeight: 700 }}>⏳ Zeitraum wird geladen … (lange Zeiträume können dauern)</span>}
        </div>
      </div>

      {/* ── Zeitraum-Steuerung ── */}
      <div style={S.controlBar}>
        <div style={S.presets}>
          {PRESETS.map(p => (
            <button key={p.label}
              onClick={() => { setHours(p.hours); setActivePreset(p.label); setCustomMode(false); }}
              style={{ ...S.presetBtn, background: activePreset === p.label && !customMode ? "#1d4ed8" : "#1e3a5f" }}>
              {p.label}
            </button>
          ))}
          <button onClick={() => setCustomMode(!customMode)}
            style={{ ...S.presetBtn, background: customMode ? "#1d4ed8" : "#1e3a5f" }}>
            Zeitraum…
          </button>
        </div>
      </div>

      {customMode && (
        <div style={S.dateRow}>
          <label style={S.dateLabel}>Von</label>
          <input type="datetime-local" value={dateFrom} min={earliest || ""}
            onChange={e => setDateFrom(e.target.value)} style={S.dateInput} />
          <label style={S.dateLabel}>Bis</label>
          <input type="datetime-local" value={dateTo}
            onChange={e => setDateTo(e.target.value)} style={S.dateInput} />
          <button onClick={loadData} style={S.goBtn}>Anzeigen</button>
          {earliest && <span style={S.hint}>Daten ab {earliest.replace("T", " ")}</span>}
        </div>
      )}

      {/* ── Sensor-Legende (Toggle) ── */}
      <div style={S.legendRow}>
        <span style={S.legendLabel}>Sensoren:</span>
        {activeSensors.map((s, si) => (
          <button key={s.id} onClick={() => toggleSensor(s.name)}
            style={{
              ...S.legendBtn,
              opacity: visibleSensors[s.name] ? 1 : 0.35,
              borderColor: colorOf(s.name),
            }}>
            <span style={{ ...S.legendDot, background: colorOf(s.name) }} />
            {s.name}
            {sensorLage(s) && <span style={S.legendSub}>{sensorLage(s)}</span>}
          </button>
        ))}
        <button onClick={() => {
          const allOn = activeSensors.every(s => visibleSensors[s.name]);
          const v = {};
          activeSensors.forEach(s => { v[s.name] = !allOn; });
          setVisibleSensors(v);
        }} style={S.toggleAllBtn}>
          {activeSensors.every(s => visibleSensors[s.name]) ? "Alle aus" : "Alle ein"}
        </button>
      </div>

      {/* ── Abschnitts-Navigation ── */}
      <div style={S.sectionNav}>
        {[
          { key: "dist",      label: "📈 Verteilung" },
          { key: "classes",   label: "🌬️ Windklassen" },
          { key: "simulate",  label: "🎛️ Regelungssimulation" },
          { key: "threshold", label: "Schwellenwert" },
          { key: "hourly",    label: "🕐 Stundenprofil" },
          { key: "stats",     label: "📊 Statistik" },
        ].map(sec => (
          <button key={sec.key}
            onClick={() => setActiveSection(sec.key)}
            style={{ ...S.secBtn, background: activeSection === sec.key ? "#1d4ed8" : "#1e293b",
              borderColor: activeSection === sec.key ? "#3b82f6" : "#334155" }}>
            {sec.label}
          </button>
        ))}
      </div>

      {loadError && !loading && (
        <div style={{ background: "#450a0a", border: "1px solid #ef4444", color: "#fca5a5", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
          Zeitraum konnte nicht geladen werden — die Anzeige zeigt weiterhin die zuletzt geladenen Daten. Nochmals versuchen oder kürzeren Zeitraum wählen.
        </div>
      )}
      {!data && !loading && (
        <div style={S.noData}>Keine Daten für den gewählten Zeitraum</div>
      )}

      {data && (
        <div style={{ opacity: loading ? 0.35 : 1, pointerEvents: loading ? "none" : "auto", transition: "opacity 0.25s" }}>
          {/* ═══ STATISTIK-TABELLE ═══ */}
          {activeSection === "stats" && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <span style={S.cardTitle}>Statistik im gewählten Zeitraum</span>
                <span style={S.cardHint}>Windgeschwindigkeit und Böen pro Sensor</span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Sensor</th>
                      <th style={S.th}>Position</th>
                      <th style={{ ...S.th, color: "#3b82f6" }}>Ø Wind</th>
                      <th style={{ ...S.th, color: "#3b82f6" }}>Max Wind</th>
                      <th style={{ ...S.th, color: "#3b82f6" }}>Zeitpunkt Max</th>
                      <th style={{ ...S.th, color: "#f97316" }}>Ø Böe</th>
                      <th style={{ ...S.th, color: "#f97316" }}>Max Böe</th>
                      <th style={{ ...S.th, color: "#f97316" }}>Zeitpunkt Max</th>
                      <th style={S.th}>Min Wind</th>
                      <th style={S.th}>Messungen</th>
                      <th style={S.th}>Abdeckung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSensors.map((s, si) => {
                      if (!visibleSensors[s.name]) return null;
                      const d = data[s.name];
                      if (!d || d.count === 0) return (
                        <tr key={s.id} style={{ background: si % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                          <td style={S.td}><span style={{ ...S.sensorDot, background: colorOf(s.name) }} />{s.name}</td>
                          <td style={{ ...S.td, color: "#475569" }} colSpan={10}>Keine Daten · Abdeckung 0%</td>
                        </tr>
                      );
                      const sw = d.stats_wind;
                      const sg = d.stats_gust;
                      return (
                        <tr key={s.id} style={{ background: si % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                          <td style={S.td}>
                            <span style={{ ...S.sensorDot, background: colorOf(s.name) }} />
                            <strong>{s.name}</strong>
                          </td>
                          <td style={{ ...S.td, color: "#94a3b8", fontSize: 12 }}>{sensorLage(s) || "–"}</td>
                          <td style={{ ...S.td, color: "#3b82f6", fontWeight: 600 }}>{fmt(sw?.avg)} m/s</td>
                          <td style={{ ...S.td, color: "#3b82f6", fontWeight: 700 }}>
                            <span style={{ color: windColor(sw?.max) }}>{fmt(sw?.max)} m/s</span>
                          </td>
                          <td style={{ ...S.td, color: "#94a3b8", fontSize: 12 }}>{fmtDt(sw?.max_at)}</td>
                          <td style={{ ...S.td, color: "#f97316", fontWeight: 600 }}>{fmt(sg?.avg)} m/s</td>
                          <td style={{ ...S.td, color: "#f97316", fontWeight: 700 }}>
                            <span style={{ color: windColor(sg?.max) }}>{fmt(sg?.max)} m/s</span>
                          </td>
                          <td style={{ ...S.td, color: "#94a3b8", fontSize: 12 }}>{fmtDt(sg?.max_at)}</td>
                          <td style={{ ...S.td, color: "#475569" }}>{fmt(sw?.min)} m/s</td>
                          <td style={{ ...S.td, color: "#475569" }}>{d.count.toLocaleString()}</td>
                          <td style={{ ...S.td, fontWeight: 700, color: d.coverage_pct >= 90 ? "#22c55e" : d.coverage_pct >= 50 ? "#eab308" : "#ef4444" }}>
                            {d.coverage_pct != null ? `${d.coverage_pct}%` : "–"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ STUNDENPROFIL ═══ */}
          {activeSection === "hourly" && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <span style={S.cardTitle}>Stundenprofil – Ø Windgeschwindigkeit nach Tageszeit</span>
                <span style={S.cardHint}>Durchschnitt aller Tage im Zeitraum, gruppiert nach Stunde (Lokalzeit Europe/Zürich)</span>
              </div>
              <HourlyChart
                data={data}
                sensorNames={activeSensors.map(s => s.name)}
                sensorColors={SENSOR_COLORS}
                visibleSensors={visibleSensors}
              />
              {/* Numerische Tabelle */}
              <div style={{ overflowX: "auto", marginTop: 16 }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Stunde</th>
                      {activeSensors.filter(s => visibleSensors[s.name]).map((s, si) => (
                        <th key={s.id} style={{ ...S.th, color: colorOf(s.name) }}>
                          {s.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 24 }, (_, h) => (
                      <tr key={h} style={{ background: h % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                        <td style={{ ...S.td, color: "#94a3b8", fontWeight: 600 }}>{String(h).padStart(2, "0")}:00</td>
                        {activeSensors.filter(s => visibleSensors[s.name]).map((s, si) => {
                          const hd = data[s.name]?.hourly?.[h];
                          return (
                            <td key={s.id} style={{ ...S.td, color: windColor(hd?.avg_wind) }}>
                              {hd?.avg_wind != null ? `${fmt(hd.avg_wind)} m/s` : "–"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ SCHWELLENWERT-ANALYSE ═══ */}
          {activeSection === "threshold" && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <span style={S.cardTitle}>Schwellenwert-Analyse</span>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <label style={S.dateLabel}>Schwellenwert:</label>
                  <input
                    type="number" min="0" max="50" step="0.5"
                    value={thresholdInput}
                    onChange={e => handleThresholdChange(e.target.value)}
                    style={{ ...S.dateInput, width: 70, textAlign: "center" }}
                  />
                  <span style={{ color: "#94a3b8", fontSize: 13 }}>m/s</span>
                  <input type="range" min="0" max="20" step="0.5" value={threshold}
                    onChange={e => { setThreshold(parseFloat(e.target.value)); setThresholdInput(e.target.value); }}
                    style={{ width: 120 }} />
                  <button onClick={loadData} style={S.goBtn}>Neu berechnen</button>
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Sensor</th>
                      <th style={S.th}>Position</th>
                      <th style={{ ...S.th, color: "#f97316" }}>Überschreitungen Wind</th>
                      <th style={{ ...S.th, color: "#f97316" }}>Gesamtdauer Wind</th>
                      <th style={{ ...S.th, color: "#f97316" }}>Längste Folge Wind</th>
                      <th style={{ ...S.th, color: "#f97316" }}>Anteil Wind</th>
                      <th style={{ ...S.th, color: "#ef4444" }}>Überschreitungen Böe</th>
                      <th style={{ ...S.th, color: "#ef4444" }}>Gesamtdauer Böe</th>
                      <th style={{ ...S.th, color: "#ef4444" }}>Längste Folge Böe</th>
                      <th style={{ ...S.th, color: "#ef4444" }}>Anteil Böe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSensors.map((s, si) => {
                      if (!visibleSensors[s.name]) return null;
                      const d = data[s.name];
                      if (!d || d.count === 0) return null;
                      const tw = d.threshold_wind;
                      const tg = d.threshold_gust;
                      return (
                        <tr key={s.id} style={{ background: si % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                          <td style={S.td}>
                            <span style={{ ...S.sensorDot, background: colorOf(s.name) }} />
                            <strong>{s.name}</strong>
                          </td>
                          <td style={{ ...S.td, color: "#94a3b8", fontSize: 12 }}>{sensorLage(s) || "–"}</td>
                          <td style={{ ...S.td, color: tw?.events > 0 ? "#f97316" : "#475569", fontWeight: tw?.events > 0 ? 700 : 400 }}>
                            {tw?.events ?? "–"}
                          </td>
                          <td style={{ ...S.td, color: "#94a3b8" }}>{fmtMin(tw?.total_minutes)}</td>
                          <td style={{ ...S.td, color: "#94a3b8" }}>{fmtMin(tw?.longest_minutes)}</td>
                          <td style={S.td}>
                            <PctBar pct={tw?.pct} color="#f97316" />
                          </td>
                          <td style={{ ...S.td, color: tg?.events > 0 ? "#ef4444" : "#475569", fontWeight: tg?.events > 0 ? 700 : 400 }}>
                            {tg?.events ?? "–"}
                          </td>
                          <td style={{ ...S.td, color: "#94a3b8" }}>{fmtMin(tg?.total_minutes)}</td>
                          <td style={{ ...S.td, color: "#94a3b8" }}>{fmtMin(tg?.longest_minutes)}</td>
                          <td style={S.td}>
                            <PctBar pct={tg?.pct} color="#ef4444" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ color: "#475569", fontSize: 11, marginTop: 10 }}>
                * Schwellenwert: {threshold} m/s ({(threshold * 3.6).toFixed(1)} km/h). Böen-Wert = Maximum der letzten ~10 Min. (Ecowitt-Messverfahren).
              </div>
            </div>
          )}

          {/* ═══ VERTEILUNG (1-m/s-Klassen) ═══ */}
          {activeSection === "dist" && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <span style={S.cardTitle}>Geschwindigkeits-Verteilung – 1-m/s-Klassen</span>
                <span style={S.cardHint}>Anteil der Messzeit je Klasse · Basis fuer Regel-Schwellen · Sortierung nach Lage im Gebaeude</span>
              </div>
              {[
                { key: "wind_dist", titel: "Windgeschwindigkeit", farbe: "59,130,246", textFarbe: "#3b82f6" },
                { key: "gust_dist", titel: "Boeen",               farbe: "249,115,22", textFarbe: "#f97316" },
              ].map(cfg => {
                // Verteilung: tiefste Lage links, Dach ganz rechts
                const vis = [...sortedSensors].reverse().filter(s => visibleSensors[s.name] && data[s.name]?.count > 0);
                // Letzte Klasse mit Inhalt bestimmen (mind. bis 7-8 anzeigen)
                let maxBin = 7;
                vis.forEach(s => (data[s.name]?.[cfg.key] || []).forEach((b, i) => { if (b.pct > 0) maxBin = Math.max(maxBin, i); }));
                const bins = (data[vis[0]?.name]?.[cfg.key] || []).slice(0, maxBin + 1);
                return (
                  <div key={cfg.key} style={{ marginBottom: 24 }}>
                    <div style={{ color: cfg.textFarbe, fontWeight: 600, fontSize: 13, marginBottom: 10 }}>{cfg.titel}</div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={S.table}>
                        <thead>
                          <tr>
                            <th style={S.th}>Klasse [m/s]</th>
                            {vis.map(s => (
                              <th key={s.id} style={{ ...S.th, color: colorOf(s.name) }}>
                                {s.name}
                                <div style={{ color: "#8aa2c0", fontSize: 10, fontWeight: 400 }}>{sensorLage(s) || "–"}</div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {bins.map((_, bi) => (
                            <tr key={bi} style={{ background: bi % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                              <td style={{ ...S.td, color: "#94a3b8", fontWeight: 700, whiteSpace: "nowrap" }}>
                                {(data[vis[0]?.name]?.[cfg.key] || [])[bi]?.bin}
                              </td>
                              {vis.map(s => {
                                const b = (data[s.name]?.[cfg.key] || [])[bi];
                                const pct = b?.pct ?? 0;
                                return (
                                  <td key={s.id} title={b ? `${b.n} Messungen` : ""}
                                    style={{ ...S.td, fontSize: 12,
                                      background: pct > 0 ? `rgba(${cfg.farbe},${Math.min(0.85, 0.08 + pct / 100 * 1.4)})` : "transparent",
                                      color: pct >= 20 ? "#ffffff" : pct > 0 ? "#dbeafe" : "#334466",
                                      fontWeight: pct >= 10 ? 700 : 400 }}>
                                    {pct > 0 ? `${pct}%` : "·"}
                                </td>
                              );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
              <div style={{ color: "#8aa2c0", fontSize: 11.5 }}>
                * Anteile beziehen sich auf die vorhandene Messzeit des Sensors (siehe Abdeckung in der Statistik). Klassen ohne Werte: "·".
              </div>
            </div>
          )}

          {/* ═══ WINDKLASSEN ═══ */}
          {activeSection === "classes" && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <span style={S.cardTitle}>Storen-Verfügbarkeit – Schenker AS150 Ausstellstore</span>
                <span style={S.cardHint}>EN 13561 · Windwiderstandsklasse 4 · Max. 60 km/h / 16.7 m/s · Max. Fläche: 9 m²</span>
              </div>

              {/* Legende Betriebsstufen */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {WIND_CLASSES.map(wc => (
                  <div key={wc.key} style={{
                    background: "#0f172a", border: `1px solid ${wc.color}`,
                    borderRadius: 7, padding: "6px 12px", display: "flex",
                    alignItems: "center", gap: 8,
                  }}>
                    <span style={{ fontSize: 14 }}>{wc.icon}</span>
                    <div>
                      <div style={{ color: wc.color, fontWeight: 700, fontSize: 12 }}>
                        {wc.status} – {wc.shortLabel}
                      </div>
                      <div style={{ color: "#cbd5e1", fontSize: 11 }}>{wc.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Windgeschwindigkeit */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ color: "#3b82f6", fontWeight: 600, fontSize: 13, marginBottom: 10 }}>
                  Windgeschwindigkeit – Überschreitungsdauer
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Sensor</th>
                        <th style={S.th}>Position</th>
                        {WIND_CLASSES.map(wc => (
                          <th key={wc.key} style={{ ...S.th, color: wc.color }}>
                            {wc.icon} {wc.shortLabel}
                          </th>
                        ))}
                        <th style={{ ...S.th, color: "#22c55e" }}>✅ Verfügbar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSensors.map((s, si) => {
                        if (!visibleSensors[s.name]) return null;
                        const d = data[s.name];
                        if (!d || d.count === 0) return null;
                        const totalMin = d.count * 1;
                        const availPct = d.wind_classes?.pct_over_8_3 != null
                          ? Math.max(0, 100 - d.wind_classes.pct_over_8_3).toFixed(1)
                          : null;
                        const availMin = availPct != null
                          ? Math.round(totalMin * availPct / 100) : null;
                        return (
                          <tr key={s.id} style={{ background: si % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                            <td style={S.td}>
                              <span style={{ ...S.sensorDot, background: colorOf(s.name) }} />
                              <strong>{s.name}</strong>
                            </td>
                            <td style={{ ...S.td, color: "#94a3b8", fontSize: 12 }}>{sensorLage(s) || "–"}</td>
                            {WIND_CLASSES.map(wc => {
                              const pct = d.wind_classes?.[wc.key];
                              const mins = pct != null ? Math.round(totalMin * pct / 100) : null;
                              return (
                                <td key={wc.key} style={S.td}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <PctBar pct={pct} color={wc.color} />
                                    <span style={{ color: "#8aa2c0", fontSize: 11.5 }}>
                                      {mins != null ? fmtMin(mins) : "–"}
                                    </span>
                                  </div>
                                </td>
                              );
                            })}
                            <td style={S.td}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <PctBar pct={availPct != null ? parseFloat(availPct) : null} color="#22c55e" />
                                <span style={{ color: "#8aa2c0", fontSize: 11.5 }}>
                                  {availMin != null ? fmtMin(availMin) : "–"}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Böen */}
              <div>
                <div style={{ color: "#f97316", fontWeight: 600, fontSize: 13, marginBottom: 10 }}>
                  Böen – Überschreitungsdauer
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Sensor</th>
                        <th style={S.th}>Position</th>
                        {WIND_CLASSES.map(wc => (
                          <th key={wc.key} style={{ ...S.th, color: wc.color }}>
                            {wc.icon} {wc.shortLabel}
                          </th>
                        ))}
                        <th style={{ ...S.th, color: "#22c55e" }}>✅ Verfügbar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSensors.map((s, si) => {
                        if (!visibleSensors[s.name]) return null;
                        const d = data[s.name];
                        if (!d || d.count === 0) return null;
                        const totalMin = d.count * 1;
                        const availPct = d.gust_classes?.pct_over_8_3 != null
                          ? Math.max(0, 100 - d.gust_classes.pct_over_8_3).toFixed(1)
                          : null;
                        const availMin = availPct != null
                          ? Math.round(totalMin * availPct / 100) : null;
                        return (
                          <tr key={s.id} style={{ background: si % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                            <td style={S.td}>
                              <span style={{ ...S.sensorDot, background: colorOf(s.name) }} />
                              <strong>{s.name}</strong>
                            </td>
                            <td style={{ ...S.td, color: "#94a3b8", fontSize: 12 }}>{sensorLage(s) || "–"}</td>
                            {WIND_CLASSES.map(wc => {
                              const pct = d.gust_classes?.[wc.key];
                              const mins = pct != null ? Math.round(totalMin * pct / 100) : null;
                              return (
                                <td key={wc.key} style={S.td}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <PctBar pct={pct} color={wc.color} />
                                    <span style={{ color: "#8aa2c0", fontSize: 11.5 }}>
                                      {mins != null ? fmtMin(mins) : "–"}
                                    </span>
                                  </div>
                                </td>
                              );
                            })}
                            <td style={S.td}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <PctBar pct={availPct != null ? parseFloat(availPct) : null} color="#22c55e" />
                                <span style={{ color: "#8aa2c0", fontSize: 11.5 }}>
                                  {availMin != null ? fmtMin(availMin) : "–"}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div style={{ color: "#334155", fontSize: 11, marginTop: 8 }}>
                * Schenker AS150 Ausstellstore · Norm EN 13561 · Windwiderstandsklasse 4 · Max. 60 km/h (16.7 m/s) · Max. Fläche 9 m².
                Böen-Wert = Maximum der letzten ~10 Min. (Ecowitt-Messverfahren).
                Verfügbarkeit = Anteil der Zeit unter 30 km/h Warnschwelle.
              </div>
            </div>
          )}
        {activeSection === "simulate" && (
              <SimulationTab
                sensors={activeSensors}
                dateFrom={customMode ? dateFrom : ""}
                dateTo={customMode ? dateTo : ""}
                hours={hours}
              />
            )}
        </div>
      )}
    </div>
  );
});
export default AnalysisTab;

// ── Mini-Prozentbalken ────────────────────────────────────────────
function PctBar({ pct, color }) {
  if (pct === null || pct === undefined) return <span style={{ color: "#475569" }}>–</span>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 60, height: 8, background: "#1e3a5f", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: color, borderRadius: 4 }} />
      </div>
      <span style={{ color, fontSize: 12, fontWeight: 600, minWidth: 36 }}>{pct}%</span>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────
const S = {
  page: { display: "flex", flexDirection: "column", gap: 16 },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  title: { color: "#f1f5f9", fontWeight: 700, fontSize: 18 },
  subtitle: { color: "#475569", fontSize: 13 },
  exportBtn:     { background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 },
  exportBtnFull: { background: "#0f766e", color: "#fff", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 },
  loadingText: { color: "#4a6a8a", fontSize: 12 },
  controlBar: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  presets: { display: "flex", gap: 5, flexWrap: "wrap" },
  presetBtn: { color: "#e2e8f0", border: "none", borderRadius: 5, padding: "6px 13px", fontSize: 12, cursor: "pointer", fontWeight: 600 },
  dateRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#0f1e35", borderRadius: 8, padding: "10px 14px" },
  dateLabel: { color: "#94a3b8", fontSize: 12 },
  dateInput: { background: "#1e3a5f", border: "1px solid #2a4a6a", borderRadius: 5, color: "#e2e8f0", padding: "4px 8px", fontSize: 12 },
  goBtn: { background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 5, padding: "5px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600 },
  hint: { color: "#4a6a8a", fontSize: 11 },
  legendRow: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", padding: "8px 0" },
  legendLabel: { color: "#64748b", fontSize: 12, fontWeight: 600, marginRight: 4 },
  legendBtn: { background: "transparent", border: "1px solid", borderRadius: 5, padding: "3px 10px", fontSize: 11, cursor: "pointer", color: "#c0d0e8", display: "flex", alignItems: "center", gap: 4 },
  legendDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  legendSub: { color: "#8aa2c0", fontSize: 11.5, marginLeft: 2 },
  toggleAllBtn: { background: "#1e3a5f", border: "1px solid #334155", color: "#94a3b8", borderRadius: 5, padding: "3px 10px", fontSize: 11, cursor: "pointer", marginLeft: 4 },
  sectionNav: { display: "flex", gap: 6, flexWrap: "wrap" },
  secBtn: { border: "1px solid", borderRadius: 7, padding: "7px 16px", fontSize: 13, cursor: "pointer", color: "#e2e8f0", fontWeight: 600 },
  card: { background: "#1e293b", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", gap: 12 },
  cardHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 },
  cardTitle: { color: "#f1f5f9", fontSize: 15, fontWeight: 700 },
  cardHint: { color: "#475569", fontSize: 12 },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 600 },
  th: { color: "#64748b", fontSize: 12, fontWeight: 600, textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #334155", whiteSpace: "nowrap" },
  td: { color: "#e2e8f0", fontSize: 13, padding: "9px 12px", borderBottom: "1px solid #1e3a5f", whiteSpace: "nowrap" },
  sensorDot: { display: "inline-block", width: 8, height: 8, borderRadius: "50%", marginRight: 6, flexShrink: 0 },
  noData: { color: "#475569", textAlign: "center", marginTop: 60, fontSize: 15 },
};
